/**
 * MinaRoMina Cart Drawer Functionality v2.0
 *
 * Handles AJAX cart operations, drawer open/close,
 * quantity updates, and cart state management.
 */

class MinaRominaCart {
  constructor(drawer) {
    this.drawer = drawer;
    this.overlay = drawer.querySelector('.mm-cart-drawer__overlay');
    this.closeButtons = drawer.querySelectorAll('[data-close-cart]');
    this.itemsContainer = drawer.querySelector('[data-cart-items]');
    this.cartCount = drawer.querySelector('[data-cart-count]');
    this.cartSubtotal = drawer.querySelector('[data-cart-subtotal]');
    this.cartNote = drawer.querySelector('[data-cart-note]');
    this.checkoutButton = drawer.querySelector('[data-checkout-button]');
    this.loadingOverlay = drawer.querySelector('[data-cart-loading]');

    this.isUpdating = false;

    this.init();
  }

  init() {
    // Close button handlers - USE EVENT DELEGATION for dynamic elements
    this.drawer.addEventListener('click', (e) => {
      const closeButton = e.target.closest('[data-close-cart]');
      if (closeButton) {
        e.preventDefault();
        e.stopImmediatePropagation();
        console.log('🔒 [Cart] Close button clicked via delegation');
        this.close();
        return;
      }
    }, { capture: true });

    // Overlay click to close - USE CAPTURE PHASE
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation(); // Block duplicate listeners
        this.close();
      }, { capture: true });
    }

    // Quantity change handlers
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-quantity-change]')) {
        e.preventDefault();
        const button = e.target.closest('[data-quantity-change]');
        const line = parseInt(button.dataset.quantityChange);
        const quantity = parseInt(button.dataset.quantity);
        this.updateQuantity(line, quantity);
      }
    });

    // Remove item handlers
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-item]')) {
        e.preventDefault();
        const button = e.target.closest('[data-remove-item]');
        const line = parseInt(button.dataset.removeItem);
        this.removeItem(line);
      }
    });

    // Cart note handler
    if (this.cartNote) {
      this.cartNote.addEventListener('change', () => {
        this.updateCartNote();
      });
    }

    // Checkout button handler
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }

    // ESC key to close - REMOVED FROM HERE, handled by global listener below
  }

  async updateQuantity(line, quantity) {
    if (this.isUpdating) return;

    try {
      this.setLoading(true);
      this.isUpdating = true;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: Math.max(0, quantity)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      this.updateCartDisplay(cart);

    } catch (error) {
      console.error('Cart update error:', error);
      this.showError('Failed to update cart. Please try again.');
    } finally {
      this.setLoading(false);
      this.isUpdating = false;
    }
  }

  async removeItem(line) {
    if (this.isUpdating) return;

    try {
      this.setLoading(true);
      this.isUpdating = true;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      this.updateCartDisplay(cart);

    } catch (error) {
      console.error('Cart remove error:', error);
      this.showError('Failed to remove item. Please try again.');
    } finally {
      this.setLoading(false);
      this.isUpdating = false;
    }
  }

  async updateCartNote() {
    if (!this.cartNote || this.isUpdating) return;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: this.cartNote.value
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart note');
      }

    } catch (error) {
      console.error('Cart note update error:', error);
    }
  }

  updateCartDisplay(cart) {
    // Update cart count
    if (this.cartCount) {
      this.cartCount.textContent = `(${cart.item_count})`;
    }

    // Update subtotal
    if (this.cartSubtotal) {
      this.cartSubtotal.textContent = this.formatMoney(cart.total_price);
    }

    // Update global cart count (header)
    const globalCartCounts = document.querySelectorAll('[data-cart-count-global]');
    globalCartCounts.forEach(element => {
      element.textContent = cart.item_count;
    });

    // Update individual line items
    cart.items.forEach(item => {
      const itemElement = this.drawer.querySelector(`[data-cart-item="${item.key}"]`);
      if (itemElement) {
        // Update quantity display
        const quantityDisplay = itemElement.querySelector(`[data-quantity-display="${item.key}"]`);
        if (quantityDisplay) {
          quantityDisplay.textContent = item.quantity;
        }

        // Update line total
        const lineTotal = itemElement.querySelector(`[data-line-total="${item.key}"]`);
        if (lineTotal) {
          lineTotal.textContent = this.formatMoney(item.final_line_price);
        }

        // Update quantity buttons
        const decreaseBtn = itemElement.querySelector(`[data-quantity-change="${item.key}"][data-quantity="${item.quantity - 1}"]`);
        if (decreaseBtn) {
          decreaseBtn.disabled = item.quantity <= 1;
          decreaseBtn.dataset.quantity = item.quantity - 1;
        }

        const increaseBtn = itemElement.querySelector(`[data-quantity-change="${item.key}"][data-quantity="${item.quantity + 1}"]`);
        if (increaseBtn) {
          increaseBtn.dataset.quantity = item.quantity + 1;
        }
      }
    });

    // Handle removed items
    const currentItems = this.drawer.querySelectorAll('[data-cart-item]');
    currentItems.forEach(itemElement => {
      const key = itemElement.dataset.cartItem;
      const stillInCart = cart.items.some(item => item.key === key);
      if (!stillInCart) {
        itemElement.remove();
      }
    });

    // Show empty state if no items
    if (cart.items.length === 0) {
      this.showEmptyState();
    }

    // Trigger custom event for other components
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));
  }

  showEmptyState() {
    if (this.itemsContainer) {
      this.itemsContainer.innerHTML = `
        <div class="mm-cart-drawer__empty" data-cart-empty>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="17" cy="34" r="3"></circle>
            <circle cx="35" cy="34" r="3"></circle>
            <path d="M5 7h3l3 15h26l3-12H14"></path>
          </svg>
          <h3>Your cart is empty</h3>
          <p>Start shopping to add items to your cart.</p>
          <a href="/collections/all" class="mm-btn mm-btn--primary" data-close-cart>
            Continue Shopping
          </a>
        </div>
      `;

      // Hide footer when empty
      const footer = this.drawer.querySelector('.mm-cart-drawer__footer');
      if (footer) {
        footer.style.display = 'none';
      }
    }
  }

  async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      this.setLoading(true);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add to cart');
      }

      // Get updated cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      this.updateCartDisplay(cart);
      this.open();

      return { success: true, cart };

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(error.message || 'Failed to add item to cart');
      return { success: false, error: error.message };
    } finally {
      this.setLoading(false);
    }
  }

  open() {
    console.log('🚀 [Cart] open() method called');
    console.log('🔍 [Cart] Drawer element:', this.drawer);
    const computedBefore = window.getComputedStyle(this.drawer);
    console.log('🔍 [Cart] Drawer computed display BEFORE:', computedBefore.display);
    console.log('🔍 [Cart] Drawer computed opacity BEFORE:', computedBefore.opacity);
    console.log('🔍 [Cart] Drawer computed visibility BEFORE:', computedBefore.visibility);
    console.log('🔍 [Cart] Drawer computed transform BEFORE:', computedBefore.transform);
    console.log('🔍 [Cart] Drawer computed width BEFORE:', computedBefore.width);
    console.log('🔍 [Cart] Drawer computed height BEFORE:', computedBefore.height);
    console.log('🔍 [Cart] Drawer computed left BEFORE:', computedBefore.left);
    console.log('🔍 [Cart] Drawer computed top BEFORE:', computedBefore.top);

    // CRITICAL FIX: Force overlay-group to be visible first
    const overlayGroup = document.querySelector('.shopify-section-group-overlay-group');
    if (overlayGroup) {
      overlayGroup.style.cssText = 'display: block !important; position: fixed !important; inset: 0 !important; z-index: 10000 !important; pointer-events: none !important;';
      console.log('🔧 [Cart] Overlay group forced visible');
    }

    this.drawer.style.display = 'flex';
    this.drawer.style.opacity = '1';
    this.drawer.style.visibility = 'visible';
    this.drawer.style.pointerEvents = 'auto';
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // CRITICAL FIX: Force container and scroll for mobile
    const container = this.drawer.querySelector('.mm-cart-drawer__container');
    if (container) {
      container.style.cssText = `
        display: flex !important;
        flex-direction: column !important;
        width: min(460px, 92vw) !important;
        height: 100dvh !important;
        max-height: 100dvh !important;
        overflow: hidden !important;
      `;
      console.log('🔧 [Cart] Container forced visible with scroll fix');
    }

    // CRITICAL FIX: Content wrapper flex column
    const content = this.drawer.querySelector('.mm-cart-drawer__content');
    if (content) {
      content.style.cssText = `
        display: flex !important;
        flex-direction: column !important;
        height: 100% !important;
        overflow: hidden !important;
      `;
    }

    // CRITICAL FIX: Items area - SCROLLABLE
    const items = this.drawer.querySelector('.mm-cart-drawer__items');
    if (items) {
      items.style.cssText = `
        flex: 1 1 auto !important;
        overflow-y: auto !important;
        min-height: 0 !important;
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior: contain !important;
      `;
      console.log('🔧 [Cart] Items area scroll enabled');
    }

    // CRITICAL FIX: Footer fixed at bottom
    const footer = this.drawer.querySelector('.mm-cart-drawer__footer');
    if (footer) {
      footer.style.cssText = `
        flex-shrink: 0 !important;
        margin-top: auto !important;
      `;
      console.log('🔧 [Cart] Footer fixed at bottom');
    }

    const computedAfter = window.getComputedStyle(this.drawer);
    console.log('🔍 [Cart] Drawer computed display AFTER:', computedAfter.display);
    console.log('🔍 [Cart] Drawer computed opacity AFTER:', computedAfter.opacity);
    console.log('🔍 [Cart] Drawer computed visibility AFTER:', computedAfter.visibility);

    // Add opening animation class
    requestAnimationFrame(() => {
      this.drawer.classList.add('mm-cart-drawer--open');
      console.log('🎨 [Cart] Added mm-cart-drawer--open class');
      const computedFinal = window.getComputedStyle(this.drawer);
      console.log('🔍 [Cart] FINAL display:', computedFinal.display);
      console.log('🔍 [Cart] FINAL opacity:', computedFinal.opacity);
      console.log('🔍 [Cart] FINAL visibility:', computedFinal.visibility);
      console.log('🔍 [Cart] FINAL transform:', computedFinal.transform);
    });
  }

  close() {
    // Blur active element BEFORE closing to fix ARIA warning
    if (document.activeElement && this.drawer.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.drawer.classList.remove('mm-cart-drawer--open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Hide after animation
    setTimeout(() => {
      if (!this.drawer.classList.contains('mm-cart-drawer--open')) {
        this.drawer.style.display = 'none';
      }
    }, 300);
  }

  isOpen() {
    return this.drawer.getAttribute('aria-hidden') === 'false';
  }

  setLoading(loading) {
    if (this.loadingOverlay) {
      this.loadingOverlay.hidden = !loading;
    }
  }

  showError(message) {
    // Simple error notification (can be enhanced)
    console.error('Cart error:', message);

    // Could add a toast notification here
    const errorDiv = document.createElement('div');
    errorDiv.className = 'mm-cart-error';
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
    `;

    document.body.appendChild(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  formatMoney(cents) {
    // Basic money formatting - should be enhanced with proper locale support
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Global cart instance
window.cartDrawer = null;

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const drawer = document.querySelector('.mm-cart-drawer');
  if (drawer) {
    window.cartDrawer = new MinaRominaCart(drawer);
  }

  // Global cart triggers (for header cart icon, etc.)
  const cartTriggers = document.querySelectorAll('[data-open-cart]');
  cartTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (window.cartDrawer) {
        window.cartDrawer.open();
      }
    });
  });

  // Listen for add to cart events from other components
  document.addEventListener('product:added-to-cart', (e) => {
    if (cartDrawer && e.detail) {
      cartDrawer.addToCart(e.detail.variantId, e.detail.quantity || 1);
    }
  });
});

// Export for use by other scripts
window.MinaRominaCart = MinaRominaCart;
window.cartDrawer = cartDrawer;

// === CREATE WORKING OVERLAY (SAFE VERSION) ===
(function() {
  const drawer = document.querySelector('.mm-cart-drawer');
  if (!drawer) return;

  // Creamos overlay solo como capa visual, NO atrapamos todos los clics
  let overlay = drawer.querySelector('.mrm-working-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'mrm-working-overlay';
    overlay.style.cssText = `
      position: fixed !important;
      inset: 0 !important;
      background: rgba(0, 0, 0, 0.5) !important;
      z-index: 9998 !important;
      cursor: default !important;
      pointer-events: none !important;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    drawer.insertBefore(overlay, drawer.firstChild);
  }

  // Aseguramos que el spinner y loading overlay no bloqueen clics
  const spinner = drawer.querySelector('.mm-cart-drawer__spinner');
  if (spinner) {
    spinner.style.pointerEvents = 'none';
  }
  const loadingOverlay = drawer.querySelector('.mm-cart-drawer__loading');
  if (loadingOverlay) {
    loadingOverlay.style.pointerEvents = 'none';
  }

  // NO capturamos clics globales ni usamos stopImmediatePropagation
  // El cierre del drawer se hace con el botón de cerrar y lógica existente

  console.log('MRM RESCUE FIX APLICADO – clicks desbloqueados, cart y menú revisados');
})();

// Listen for cart update events from product add to cart
document.addEventListener('mm:cart:update', async (e) => {
  console.log('🛒 [Cart] mm:cart:update event received:', e.detail);

  if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
    console.log('🛒 [Cart] Opening cart drawer...');

    try {
      // Get section ID from drawer
      const drawer = window.cartDrawer.drawer;
      const sectionId = drawer ? drawer.dataset.sectionId : null;

      if (sectionId) {
        console.log('🔄 [Cart] Fetching fresh HTML from section:', sectionId);

        // Fetch fresh HTML from Section Rendering API
        const response = await fetch(`/?section_id=${sectionId}`);
        const html = await response.text();

        // Parse the HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;

        // Extract the new drawer content
        const newDrawer = temp.querySelector('.mm-cart-drawer');
        if (newDrawer) {
          const oldContainer = drawer.querySelector('.mm-cart-drawer__container');
          const newContainer = newDrawer.querySelector('.mm-cart-drawer__container');

          if (oldContainer && newContainer) {
            // Replace entire container to get fresh HTML with translations
            oldContainer.innerHTML = newContainer.innerHTML;
            console.log('✅ [Cart] Drawer updated with fresh HTML');
          }
        }
      } else {
        console.warn('⚠️ [Cart] No section ID found, using basic update');
        // Fallback to basic update
        const response = await fetch('/cart.js');
        const cart = await response.json();
        window.cartDrawer.updateCartDisplay(cart);
      }

      // Update global cart count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const globalCartCounts = document.querySelectorAll('[data-cart-count-global]');
      globalCartCounts.forEach(element => {
        element.textContent = cart.item_count;
        // Show/hide badge based on count
        if (cart.item_count > 0) {
          element.style.display = '';
        } else {
          element.style.display = 'none';
        }
      });

      // Open drawer
      window.cartDrawer.open();
      console.log('✅ [Cart] Cart drawer opened successfully');

    } catch (error) {
      console.error('❌ [Cart] Error refreshing cart:', error);
      // Still try to open drawer even if refresh fails
      window.cartDrawer.open();
    }
  }
});

console.log('✅ [Cart] Event listener for mm:cart:update registered');